import React, { FC, useState, useCallback } from 'react';
import { TopUpProduct } from '@dreamai/types';
import './top-up-modal.scss';

interface TopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    topUp: TopUpProduct,
    quantity: number,
    autoTopupCap: number | null,
  ) => void;
  topUp: TopUpProduct | null;
  cardInfo: { last4: string; brand: string } | null;
  isSubmitting: boolean;
  error?: string | null;
  /** Show the "enable auto top-up" opt-in (hidden when already enabled) */
  offerAutoTopup?: boolean;
  /** Pre-fill the auto top-up opt-in from existing settings (share-link page) */
  initialAutoTopup?: { enabled: boolean; monthlyLimit?: number };
  /** Hide the close button and disable overlay dismissal (share-link page) */
  hideClose?: boolean;
  /** Confirm button label (defaults to "Top up my account") */
  confirmLabel?: string;
}

const CAP_PRESETS: Record<string, number[]> = {
  GBP: [30, 60, 150],
  USD: [40, 80, 200],
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
};

const BRAND_ICONS: Record<string, string> = {
  visa: '/card-icons/visa.svg',
  mastercard: '/card-icons/mastercard.svg',
  'american express': '/card-icons/amex.svg',
  amex: '/card-icons/amex.svg',
  maestro: '/card-icons/maestro.svg',
  jcb: '/card-icons/jcb.svg',
  discover: '/card-icons/discover.svg',
};

const GENERIC_CARD_ICON = '/card-icons/generic.svg';

function formatPrice(amount: number, currency: string): string {
  const symbol =
    currency.toUpperCase() === 'GBP'
      ? '£'
      : currency.toUpperCase() === 'USD'
        ? '$'
        : currency.toUpperCase() === 'EUR'
          ? '€'
          : `${currency} `;
  const withTax = Math.round(amount * 1.2 * 100) / 100;
  const display = withTax.toFixed(2).replace(/\.00$/, '');
  return `${symbol}${display}`;
}

const TopUpModal: FC<TopUpModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  topUp,
  cardInfo,
  isSubmitting,
  error,
  offerAutoTopup,
  initialAutoTopup,
  hideClose,
  confirmLabel,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [autoTopupChecked, setAutoTopupChecked] = useState(
    !!initialAutoTopup?.enabled,
  );
  const [capChoice, setCapChoice] = useState<number | 'custom'>(() => {
    const limit = initialAutoTopup?.monthlyLimit;
    if (!limit) return 0;
    const presets =
      CAP_PRESETS[(topUp?.currency || 'GBP').toUpperCase()] || CAP_PRESETS.GBP;
    return presets.includes(limit) ? limit : 'custom';
  });
  const [customCap, setCustomCap] = useState(() => {
    const limit = initialAutoTopup?.monthlyLimit;
    if (!limit) return '';
    const presets =
      CAP_PRESETS[(topUp?.currency || 'GBP').toUpperCase()] || CAP_PRESETS.GBP;
    return presets.includes(limit) ? '' : String(limit);
  });

  const handleClose = useCallback(() => {
    setQuantity(1);
    setAutoTopupChecked(false);
    setCapChoice(0);
    setCustomCap('');
    onClose();
  }, [onClose]);

  if (!isOpen || !topUp) return null;

  const currency = topUp.currency.toUpperCase();
  const capPresets = CAP_PRESETS[currency] || CAP_PRESETS.GBP;
  const capSymbol = CURRENCY_SYMBOLS[currency] || `${currency} `;
  const selectedCap =
    capChoice === 'custom'
      ? parseFloat(customCap) || 0
      : capChoice || capPresets[0];
  const autoTopupCap =
    offerAutoTopup && autoTopupChecked && selectedCap > 0 ? selectedCap : null;

  const totalCredits = topUp.creditsPerUnit * quantity;
  const totalPrice = topUp.unitAmount * quantity;

  const canDecrease = quantity > 1;

  return (
    <div
      className='topup-modal-overlay'
      onClick={hideClose ? undefined : handleClose}
    >
      <div className='topup-modal' onClick={(e) => e.stopPropagation()}>
        <div className='topup-modal-header'>
          <p className='topup-modal-title'>Top-up credits</p>
          {!hideClose && (
            <button className='topup-close-button' onClick={handleClose}>
              <svg
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#374562'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <line x1='18' y1='6' x2='6' y2='18' />
                <line x1='6' y1='6' x2='18' y2='18' />
              </svg>
            </button>
          )}
        </div>

        <div className='topup-modal-body'>
          <div className='topup-amount-block'>
            <p className='topup-amount-label'>Top-up amount</p>
            <div className='topup-stepper'>
              <button
                className='topup-qty-btn'
                disabled={!canDecrease}
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                aria-label='Decrease top-up amount'
              >
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <circle cx='12' cy='12' r='10' />
                  <line x1='8' y1='12' x2='16' y2='12' />
                </svg>
              </button>
              <span className='topup-stepper-value'>
                {totalCredits.toLocaleString('en-GB')} credits
              </span>
              <button
                className='topup-qty-btn'
                onClick={() => setQuantity((q) => q + 1)}
                aria-label='Increase top-up amount'
              >
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <circle cx='12' cy='12' r='10' />
                  <line x1='12' y1='8' x2='12' y2='16' />
                  <line x1='8' y1='12' x2='16' y2='12' />
                </svg>
              </button>
            </div>
            <p className='topup-description'>
              We&rsquo;ll add {totalCredits.toLocaleString('en-GB')} credits at{' '}
              {formatPrice(totalPrice, topUp.currency)}
            </p>
          </div>

          {cardInfo && (
            <div className='topup-card-row'>
              <img
                className='card-brand-icon'
                src={
                  BRAND_ICONS[cardInfo.brand.toLowerCase()] || GENERIC_CARD_ICON
                }
                alt={cardInfo.brand}
              />
              <span className='card-ending'>Card ending {cardInfo.last4}</span>
            </div>
          )}

          {offerAutoTopup && (
            <div className='topup-autotopup-optin'>
              <label className='topup-autotopup-checkbox'>
                <input
                  type='checkbox'
                  checked={autoTopupChecked}
                  onChange={(e) => setAutoTopupChecked(e.target.checked)}
                />
                <span>
                  Automatically top up when I run low on credits (in blocks of{' '}
                  {topUp.creditsPerUnit.toLocaleString('en-GB')} credits)
                </span>
              </label>
              {autoTopupChecked && (
                <div className='topup-autotopup-caps'>
                  <span className='topup-autotopup-caps-label'>
                    Monthly limit
                  </span>
                  {capPresets.map((preset) => (
                    <button
                      key={preset}
                      className={`topup-cap-option${
                        (capChoice || capPresets[0]) === preset
                          ? ' selected'
                          : ''
                      }`}
                      onClick={() => setCapChoice(preset)}
                    >
                      {capSymbol}
                      {preset}
                    </button>
                  ))}
                  {capChoice === 'custom' ? (
                    <span className='topup-cap-option custom-input selected'>
                      {capSymbol}
                      <input
                        type='number'
                        value={customCap}
                        onChange={(e) => setCustomCap(e.target.value)}
                        autoFocus
                        min='1'
                      />
                    </span>
                  ) : (
                    <button
                      className='topup-cap-option'
                      onClick={() => setCapChoice('custom')}
                    >
                      Custom
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {error && <p className='topup-modal-error'>{error}</p>}

          <button
            className='topup-confirm-btn'
            onClick={() => onConfirm(topUp, quantity, autoTopupCap)}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? 'Processing...'
              : confirmLabel || 'Top up my account'}
          </button>

          <p className='topup-legal'>
            By purchasing this top-up, you agree to immediate delivery and waive
            your 14-day cancellation right. Credits are non-refundable once
            used.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TopUpModal;
